/**
 * Utility functions for handling timestamps
 */

/**
 * Format a timestamp for display
 */
export function formatTimestamp(timestamp: string | Date, format: 'relative' | 'absolute' | 'date' | 'time' = 'relative'): string {
  if (!timestamp) return 'Unknown';
  
  const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
  
  if (isNaN(date.getTime())) {
    return 'Invalid date';
  }

  switch (format) {
    case 'relative':
      return formatRelativeTime(date);
    case 'absolute':
      return formatAbsoluteTime(date);
    case 'date':
      return formatDate(date);
    case 'time':
      return formatTime(date);
    default:
      return formatRelativeTime(date);
  }
}

/**
 * Format timestamp as relative time (e.g., "2 hours ago", "3 days ago")
 */
function formatRelativeTime(date: Date): string {
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) {
    return 'Just now';
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return `${diffInMinutes} minute${diffInMinutes === 1 ? '' : 's'} ago`;
  }

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return `${diffInHours} hour${diffInHours === 1 ? '' : 's'} ago`;
  }

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) {
    return `${diffInDays} day${diffInDays === 1 ? '' : 's'} ago`;
  }

  const diffInWeeks = Math.floor(diffInDays / 7);
  if (diffInWeeks < 4) {
    return `${diffInWeeks} week${diffInWeeks === 1 ? '' : 's'} ago`;
  }

  const diffInMonths = Math.floor(diffInDays / 30);
  if (diffInMonths < 12) {
    return `${diffInMonths} month${diffInMonths === 1 ? '' : 's'} ago`;
  }

  const diffInYears = Math.floor(diffInDays / 365);
  return `${diffInYears} year${diffInYears === 1 ? '' : 's'} ago`;
}

/**
 * Format timestamp as absolute time
 */
function formatAbsoluteTime(date: Date): string {
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short'
  });
}

/**
 * Format timestamp as date only
 */
function formatDate(date: Date): string {
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}

/**
 * Format timestamp as time only
 */
function formatTime(date: Date): string {
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit'
  });
}

/**
 * Check if a timestamp is recent (within the last 24 hours)
 */
export function isRecent(timestamp: string | Date): boolean {
  if (!timestamp) return false;
  
  const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
  const now = new Date();
  const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
  
  return diffInHours < 24;
}

/**
 * Check if a timestamp is today
 */
export function isToday(timestamp: string | Date): boolean {
  if (!timestamp) return false;
  
  const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
  const today = new Date();
  
  return date.toDateString() === today.toDateString();
}

/**
 * Get the age of a profile in days
 */
export function getProfileAge(createdAt: string | Date): number {
  if (!createdAt) return 0;
  
  const created = typeof createdAt === 'string' ? new Date(createdAt) : createdAt;
  const now = new Date();
  const diffInDays = Math.floor((now.getTime() - created.getTime()) / (1000 * 60 * 60 * 24));
  
  return Math.max(0, diffInDays);
}

/**
 * Format profile age for display
 */
export function formatProfileAge(createdAt: string | Date): string {
  const ageInDays = getProfileAge(createdAt);
  
  if (ageInDays === 0) {
    return 'Today';
  } else if (ageInDays === 1) {
    return 'Yesterday';
  } else if (ageInDays < 7) {
    return `${ageInDays} days ago`;
  } else if (ageInDays < 30) {
    const weeks = Math.floor(ageInDays / 7);
    return `${weeks} week${weeks === 1 ? '' : 's'} ago`;
  } else if (ageInDays < 365) {
    const months = Math.floor(ageInDays / 30);
    return `${months} month${months === 1 ? '' : 's'} ago`;
  } else {
    const years = Math.floor(ageInDays / 365);
    return `${years} year${years === 1 ? '' : 's'} ago`;
  }
} 